import { create } from "zustand";

import type { ComposerMode, Filters, User } from "./types";

type Theme = "cyber" | "base";

interface Toast {
  message: string;
  type: "success" | "error";
}

function isTheme(value: unknown): value is Theme {
  return value === "cyber" || value === "base";
}

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "cyber";
  const stored = window.localStorage.getItem("mako-theme");
  return isTheme(stored) ? stored : "cyber";
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.add("theme-transition");
  root.dataset.theme = theme;
  window.setTimeout(() => {
    root.classList.remove("theme-transition");
  }, 220);
}

type View =
  | "sessions"
  | "preferences"
  | "automations"
  | "automation-detail"
  | "watches"
  | "stats"
  | "docs"
  | "admin"
  | "cli-auth";

interface AppState {
  // User state
  user: User | null;
  setUser: (user: User | null) => void;

  // View state
  activeView: View;
  activeAutomationId: string | null;
  activeDocSlug: string | null;
  setView: (view: View) => void;
  selectAutomation: (id: string) => void;
  setDocSlug: (slug: string) => void;

  // Session state
  activeSessionKey: string | null;
  selectSession: (key: string) => void;
  clearSession: () => void;

  // Sidebar state (for mobile)
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;

  // Sidebar collapsed state (for desktop)
  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;

  // Filter state
  filters: Filters;
  setFilter: (key: keyof Filters, value: string | boolean | undefined) => void;
  toggleMine: () => void;
  clearFilters: () => void;

  // Toast state
  toast: Toast | null;
  showToast: (message: string, type: "success" | "error") => void;
  hideToast: () => void;

  // Theme state
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  // Composer state
  composerMode: ComposerMode;
  composerModelId: string | null;
  setComposerMode: (mode: ComposerMode) => void;
  setComposerModelId: (modelId: string | null) => void;
}

const initialTheme = getInitialTheme();
applyTheme(initialTheme);

const defaultFilters: Filters = { mine: true, includeAutomated: false };

export const useStore = create<AppState>((set) => ({
  // User state
  user: null,
  setUser: (user) => set({ user }),

  // View state
  activeView: "sessions",
  activeAutomationId: null,
  activeDocSlug: null,
  setView: (view) =>
    set({
      activeView: view,
      activeAutomationId: null,
      activeDocSlug: null,
      activeSessionKey: null,
    }),
  selectAutomation: (id) => {
    set({
      activeView: "automation-detail",
      activeAutomationId: id,
      activeDocSlug: null,
      activeSessionKey: null,
    });
    const url = new URL(window.location.href);
    url.searchParams.delete("thread");
    url.searchParams.delete("view");
    url.searchParams.delete("doc");
    url.searchParams.set("automation", id);
    window.history.pushState({}, "", url);
  },
  setDocSlug: (slug) => {
    set({
      activeView: "docs",
      activeDocSlug: slug,
      activeAutomationId: null,
      activeSessionKey: null,
    });
    const url = new URL(window.location.href);
    url.searchParams.delete("thread");
    url.searchParams.delete("automation");
    url.searchParams.set("view", "docs");
    url.searchParams.set("doc", slug);
    window.history.pushState({}, "", url);
  },

  // Session state
  activeSessionKey: null,
  selectSession: (key) => {
    set({
      activeSessionKey: key,
      activeView: "sessions",
      activeAutomationId: null,
      activeDocSlug: null,
    });
    // Update URL with thread param
    const url = new URL(window.location.href);
    url.searchParams.set("thread", key);
    url.searchParams.delete("automation");
    url.searchParams.delete("view");
    url.searchParams.delete("doc");
    window.history.pushState({}, "", url);
  },
  clearSession: () => {
    set({ activeSessionKey: null });
    // Clear thread param from URL
    const url = new URL(window.location.href);
    url.searchParams.delete("thread");
    url.searchParams.delete("automation");
    url.searchParams.delete("view");
    url.searchParams.delete("doc");
    window.history.pushState({}, "", url);
  },

  // Sidebar state (for mobile)
  sidebarOpen: false,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  closeSidebar: () => set({ sidebarOpen: false }),

  // Sidebar collapsed state (for desktop)
  sidebarCollapsed: false,
  toggleSidebarCollapsed: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  // Filter state
  filters: defaultFilters,
  setFilter: (key, value) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [key]: value || undefined,
      },
    })),
  toggleMine: () =>
    set((state) => ({
      filters: {
        ...state.filters,
        mine: !state.filters.mine,
      },
    })),
  clearFilters: () => set({ filters: defaultFilters }),

  // Toast state
  toast: null,
  showToast: (message, type) => {
    set({ toast: { message, type } });
    setTimeout(() => set({ toast: null }), 3000);
  },
  hideToast: () => set({ toast: null }),

  // Theme state
  theme: initialTheme,
  setTheme: (theme) => {
    set({ theme });
    applyTheme(theme);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mako-theme", theme);
    }
  },
  toggleTheme: () =>
    set((state) => {
      const next = state.theme === "cyber" ? "base" : "cyber";
      applyTheme(next);
      if (typeof window !== "undefined") {
        window.localStorage.setItem("mako-theme", next);
      }
      return { theme: next };
    }),

  // Composer state
  composerMode: "build",
  composerModelId: null,
  setComposerMode: (mode) => set({ composerMode: mode }),
  setComposerModelId: (modelId) => set({ composerModelId: modelId }),
}));
